/**
 * Reading the backend's error envelopes.
 *
 * The `api` client already turns a non-2xx into an `ApiError` carrying the
 * best single line it could find. What is here is the one case that needs
 * more than a line: a `validation_failed` 400, where the message itself says
 * only "Validation failed" and the useful part is a field at a time
 * underneath it.
 *
 * It lives in `lib/api` rather than in whichever feature first needed it,
 * because the envelope belongs to the API rather than to any one form — the
 * same reasoning that moved `PaginatedResponse` here.
 */

import { ApiError } from "../http";

/**
 * The field-level problems out of a `validation_failed` body, as one line:
 * `email: Enter a valid email address. phone_number: …`.
 *
 * `undefined` when the body carries no `details` worth reading, which leaves
 * the caller with the `ApiError`'s own message.
 */
export const validationMessage = (error: ApiError): string | undefined => {
  const body = error.data;
  if (!body || typeof body !== "object") return undefined;

  const details = (body as { details?: unknown }).details;
  if (!details || typeof details !== "object") return undefined;

  const problems = Object.entries(details as Record<string, unknown>)
    .map(([field, value]) => {
      const text = Array.isArray(value) ? value[0] : value;
      return typeof text === "string" && text.trim()
        ? `${field}: ${text.trim()}`
        : "";
    })
    .filter(Boolean);

  return problems.length > 0 ? problems.join(" ") : undefined;
};

/**
 * The same, already folded back into an `ApiError` — what a service throws on
 * when it wants the fields named rather than "Validation failed". An error
 * carrying no field detail is handed back untouched.
 */
export const withValidationDetail = (error: unknown): unknown => {
  if (!(error instanceof ApiError)) return error;

  const detailed = validationMessage(error);
  return detailed
    ? new ApiError(detailed, error.status, error.data)
    : error;
};
